import { useState } from "react";
import { cn } from "../utils/cn";
import { Tooltip } from "./Tooltip";

// ── Icons ─────────────────────────────────────────────────────────────────────

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" className={className}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function CollapseIcon({ flipped }: { flipped?: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
      className={cn("transition-transform duration-300", flipped && "rotate-180")}>
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface SidebarChild {
  label: string;
  href: string;
  isActive?: boolean;
}

export type BadgeVariant = "red" | "amber" | "blue";

export interface SidebarItem {
  label: string;
  icon: React.ReactNode;
  href: string;
  badge?: string | number;
  badgeVariant?: BadgeVariant;
  isActive?: boolean;
  children?: SidebarChild[];
}

export interface SidebarProps {
  items: SidebarItem[];
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  header?: React.ReactNode;
  footer?: React.ReactNode;
}

// ── Badge color map ───────────────────────────────────────────────────────────

const BADGE_COLOR: Record<BadgeVariant, string> = {
  red:   "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400 border border-red-200 dark:border-red-900/60",
  amber: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400 border border-amber-200 dark:border-amber-900/60",
  blue:  "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400 border border-blue-200 dark:border-blue-900/60",
};

// ── Sub-item ──────────────────────────────────────────────────────────────────

function SubItem({ child }: { child: SidebarChild }) {
  return (
    <a
      href={child.href}
      aria-current={child.isActive ? "page" : undefined}
      className={cn(
        "flex items-center gap-2 pl-10 pr-3 py-2 rounded-md text-sm transition-colors duration-150",
        child.isActive
          ? "text-brand-purple-text bg-sidebar-active font-medium"
          : "text-content-secondary hover:text-content-primary hover:bg-surface-overlay dark:hover:bg-sidebar-hover",
      )}
    >
      <span className={cn(
        "w-1.5 h-1.5 rounded-full shrink-0",
        child.isActive ? "bg-violet-400 dark:bg-brand-purple-text" : "bg-content-tertiary",
      )} />
      {child.label}
    </a>
  );
}

// ── Nav item ──────────────────────────────────────────────────────────────────

interface NavItemProps {
  item: SidebarItem;
  isCollapsed: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}

function NavItemRow({ item, isCollapsed, isExpanded, onToggle }: NavItemProps) {
  const hasChildren = !!item.children?.length;
  const badgeVariant = item.badgeVariant ?? "blue";

  const iconContent = (
    <span className={cn(
      "shrink-0 flex items-center justify-center w-5 h-5 transition-colors",
      item.isActive
        ? "text-violet-500 dark:text-brand-purple-text"
        : "text-content-tertiary",
    )}>
      {item.icon}
    </span>
  );

  const itemContent = (
    <a
      href={hasChildren ? undefined : item.href}
      role={hasChildren ? "button" : undefined}
      aria-expanded={hasChildren ? isExpanded : undefined}
      aria-current={item.isActive ? "page" : undefined}
      onClick={hasChildren ? onToggle : undefined}
      className={cn(
        "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 w-full",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/50 focus-visible:ring-offset-1",
        item.isActive
          ? [
              "bg-violet-50 dark:bg-sidebar-active",
              "text-violet-700 dark:text-brand-purple-text",
              "border border-violet-200/60 dark:border-brand-purple/20",
              "font-medium",
            ].join(" ")
          : [
              "text-content-secondary",
              "hover:text-content-primary",
              "hover:bg-surface-overlay dark:hover:bg-sidebar-hover",
              "border border-transparent",
            ].join(" "),
        isCollapsed && "justify-center px-0 border-transparent",
      )}
    >
      {iconContent}

      {!isCollapsed && (
        <>
          <span className="flex-1 text-sm font-medium truncate">{item.label}</span>

          {item.badge !== undefined && (
            <span className={cn(
              "shrink-0 text-xs font-semibold px-1.5 py-0.5 rounded-full",
              BADGE_COLOR[badgeVariant],
            )}>
              {item.badge}
            </span>
          )}

          {hasChildren && (
            <ChevronDownIcon className={cn(
              "shrink-0 transition-transform duration-200 text-content-tertiary",
              isExpanded ? "rotate-180" : "",
            )} />
          )}
        </>
      )}
    </a>
  );

  if (isCollapsed) {
    return (
      <Tooltip content={item.label} side="right" delayMs={200}>
        {itemContent}
      </Tooltip>
    );
  }

  return itemContent;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function Sidebar({
  items,
  isCollapsed = false,
  onToggleCollapse,
  header,
  footer,
}: SidebarProps) {
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    items.forEach((item) => {
      if (item.children?.some((c) => c.isActive)) initial.add(item.href);
    });
    return initial;
  });

  const toggleExpanded = (href: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(href) ? next.delete(href) : next.add(href);
      return next;
    });
  };

  return (
    <aside
      className={cn(
        "flex flex-col h-full overflow-hidden transition-all duration-300 ease-in-out",
        "bg-white dark:bg-sidebar-bg",
        "border-r border-border-default dark:border-sidebar-border",
      )}
      style={{ width: isCollapsed ? 64 : 240 }}
      aria-label="Sidebar navigation"
    >
      {/* Header */}
      {header && (
        <div className={cn(
          "shrink-0 flex items-center gap-3 px-3 py-4",
          "border-b border-border-default dark:border-sidebar-border",
          isCollapsed && "justify-center px-0",
        )}>
          {header}
        </div>
      )}

      {/* Nav items */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden p-2 flex flex-col gap-0.5">
        {items.map((item) => {
          const isExp = expanded.has(item.href);
          return (
            <div key={item.href}>
              <NavItemRow
                item={item}
                isCollapsed={isCollapsed}
                isExpanded={isExp}
                onToggle={() => toggleExpanded(item.href)}
              />
              {!isCollapsed && item.children?.length && (
                <div className={cn(
                  "overflow-hidden transition-all duration-200 ease-out",
                  isExp ? "max-h-96 opacity-100" : "max-h-0 opacity-0",
                )}>
                  <div className="mt-0.5 flex flex-col gap-0.5">
                    {item.children.map((child) => (
                      <SubItem key={child.href} child={child} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Collapse toggle */}
      {onToggleCollapse && (
        <div className="shrink-0 p-2 border-t border-border-default dark:border-sidebar-border">
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={cn(
              "flex items-center gap-3 w-full px-3 py-2.5 rounded-lg transition-colors duration-150",
              "text-content-tertiary hover:text-content-primary hover:bg-surface-overlay dark:hover:bg-sidebar-hover",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/50 focus-visible:ring-offset-1",
              isCollapsed && "justify-center px-0",
            )}
          >
            <CollapseIcon flipped={isCollapsed} />
            {!isCollapsed && <span className="text-sm font-medium">Collapse</span>}
          </button>
        </div>
      )}

      {/* Footer */}
      {footer && (
        <div className={cn(
          "shrink-0 p-3 border-t border-border-default dark:border-sidebar-border",
          isCollapsed && "flex justify-center",
        )}>
          {footer}
        </div>
      )}
    </aside>
  );
}

Sidebar.displayName = "Sidebar";
